import {useCallback, useMemo} from 'react';
import {useUrl, useNavigate} from '@shopify/hydrogen';

export type SetValueFunc = (
  value: string,
  options?: {href?: string; navigate?: boolean; scroll?: boolean},
) => string;
export type UnsetValueFunc = (options?: {
  href?: string;
  navigate?: boolean;
  scroll?: boolean;
}) => string;

export function useUrlStateParam({
  param,
  defaultValue,
  array,
}: {
  param: string;
  defaultValue?: string;
  array?: Exclude<boolean, true>;
}): {
  value: string;
  setValue: SetValueFunc;
  unsetValue: UnsetValueFunc;
};
export function useUrlStateParam({
  param,
  defaultValue,
  array,
}: {
  param: string;
  defaultValue?: Array<string>;
  array?: Exclude<boolean, false>;
}): {
  value: Array<string>;
  setValue: SetValueFunc;
  unsetValue: UnsetValueFunc;
};
export function useUrlStateParam({
  param,
  defaultValue,
  array = false,
}: {
  param: string;
  defaultValue?: string | Array<string>;
  array?: boolean;
}): {
  value: string | Array<string>;
  setValue: SetValueFunc;
  unsetValue: UnsetValueFunc;
} {
  const url = useUrl();
  const navigate = useNavigate();

  const setValue: SetValueFunc = useCallback(
    (value, options) => {
      const newUrl = new URL(options?.href ?? url.href);
      newUrl.searchParams.set(param, value);
      if (options?.navigate ?? true) {
        navigate(newUrl.href, {scroll: options?.scroll});
      }
      return newUrl.href;
    },
    [navigate, param, url.href],
  );

  const unsetValue: UnsetValueFunc = useCallback(
    (options) => {
      const newUrl = new URL(options?.href ?? url.href);
      newUrl.searchParams.delete(param);
      if (options?.navigate ?? true) {
        navigate(newUrl.href, {scroll: options?.scroll});
      }
      return newUrl.href;
    },
    [navigate, param, url.href],
  );

  const value = useMemo(
    () =>
      typeof defaultValue !== 'undefined' && !url.searchParams.has(param)
        ? defaultValue
        : array
        ? url.searchParams.getAll(param)
        : url.searchParams.get(param),
    [array, defaultValue, param, url.searchParams],
  );

  return {value, setValue, unsetValue};
}
